import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { produce } from 'immer';
import { catchError, map, switchMap, throwError } from 'rxjs';
import { ConcurrencyService, MessagesService, UserService } from 'shared/domain';
import { compareArrays, compareHtmlText, tapAllResponses } from 'shared/util-common';

import { CommentHttpService } from './comment.http.service';
import { AnchoredComment, AnchoredFlowchartComment, Comment, CommentType } from './comment.model';
import { unmarkCommentOnContainer } from './comment.service.util';
import { ExpandService } from '../../../util-common/src/lib/expand.service';

/**
 * Options for initializing the CommentService.
 * @property batchEdit - Indicates if comments should be handled in batch edit mode.
 * @property comments - The initial list of anchored comments.
 * @property readonly - Indicates if the comments are in read-only mode.
 * @property headless - Indicates if the service is running next to an editor or preview
 * @property newCommentTypes - Limits the annotation types that can be newly created.
 */
export type CommentServiceInitOptions = {
  batchEdit: boolean;
  comments: AnchoredComment[];
  readonly: boolean;
  headless: boolean;
  newCommentType: CommentType;
  newCommentTypes: CommentType[];
  preserveReaderFeedbackAnchors: boolean;
  isAnchorAvailable: (comment: AnchoredComment) => boolean;
};

@Injectable()
export class CommentService {
  private currentUser = inject(UserService).user;
  private commentsHttpService = inject(CommentHttpService);
  private messagesService = inject(MessagesService);
  private expandService = inject(ExpandService, { optional: true });
  private concurrencyService = inject(ConcurrencyService, { optional: true });

  private batchEdit = true;
  private version: string;
  private guidelineId: string;
  private headless = false;
  private _newCommentType = signal(CommentType.Comment);
  public newCommentType = this._newCommentType.asReadonly();
  private newCommentTypes: CommentType[] | undefined;
  private preserveReaderFeedbackAnchors = false;
  private anchorValidator = signal<((comment: AnchoredComment) => boolean) | undefined>(undefined);

  private _initialized = signal<boolean>(false);
  public initialized = computed(() => this._initialized());

  private _readonly = signal<boolean>(true);
  public readonly = computed(() => this._readonly() || this.concurrencyService?.disabled());

  private _comments = signal<AnchoredComment[] | undefined>(undefined);
  public comments = this._comments.asReadonly();
  private commentOrderVersion = signal(0);
  private commentPositionVersion = signal(0);

  private _originalComments = signal<AnchoredComment[]>([]);
  public hasUnsavedChanges = computed(() => {
    const current = this._comments() ?? [];
    const original = this._originalComments();

    return !compareArrays(current, original);
  });

  private selectedCommentId = signal<string | undefined>(undefined);
  private _isCommentsBarOpen = signal(false);
  public isCommentsBarOpen = this._isCommentsBarOpen.asReadonly();
  public selectedCommentData = computed(() => (this._comments() ?? []).find((comment) => comment.id === this.selectedCommentId()));
  public selectedComment = computed<{ comment: AnchoredComment; yPosition: number } | null>(() => {
    this.commentPositionVersion();
    const comment = this.selectedCommentData();
    if (!comment) {
      return null;
    }
    if (this.isOriginalContentUnavailable(comment)) return null;

    const container = document.querySelector('.tt-commentable-container');
    if (!container) return null;

    let commentElement: HTMLElement;
    if (comment.anchor.type === 'flowchart-element') {
      commentElement =
        container.querySelector<HTMLElement>(`.tt-comments-comment-${comment.id}`) ??
        container.querySelector<HTMLElement>(`[unique-id="${(comment as AnchoredFlowchartComment).anchor.elementId}"]`);
    } else {
      commentElement = container.querySelector<HTMLElement>(`.tt-comments-comment-${comment.id}`);
    }

    if (!commentElement) {
      return null;
    }

    const yPosition = commentElement.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;

    return { comment, yPosition };
  });

  public orderedComments = computed(() => {
    this.commentOrderVersion();
    const comments = this._comments() || [];
    const container = document.querySelector('.tt-commentable-container');
    const commentElements = container?.querySelectorAll<HTMLElement>('[class*="tt-comments-comment-"]') ?? [];

    return comments
      .map((comment) => {
        const element = Array.from(commentElements).find((el) => el.classList.contains(`tt-comments-comment-${comment.id}`));
        return { comment, element };
      })
      .sort((a, b) => {
        if (a.element && b.element) {
          const rectA = a.element.getBoundingClientRect();
          const rectB = b.element.getBoundingClientRect();

          const topDiff = rectA.top - rectB.top;
          if (Math.abs(topDiff) > 2) {
            return topDiff;
          }

          return rectA.left - rectB.left;
        } else if (a.element) {
          return -1;
        } else if (b.element) {
          return 1;
        } else {
          return 0;
        }
      })
      .map(({ comment }) => comment);
  });

  public refreshCommentOrder(): void {
    this.commentOrderVersion.update((version) => version + 1);
    this.refreshCommentPosition();
  }

  public refreshCommentPosition(): void {
    this.commentPositionVersion.update((version) => version + 1);
  }

  public isSelectedTheLastComment = computed(() => {
    const ordered = this.orderedComments();
    if (ordered.length === 0) {
      return false;
    }

    return this.selectedCommentId() === ordered[ordered.length - 1].id;
  });

  public isSelectedTheFirstComment = computed(() => {
    const ordered = this.orderedComments();
    if (ordered.length === 0) {
      return false;
    }

    return this.selectedCommentId() === ordered[0].id;
  });

  private _addComment = rxMethod<AnchoredComment | Comment>((comment$) =>
    comment$.pipe(
      switchMap((comment) =>
        (comment.type === CommentType.ReaderFeedback
          ? this.commentsHttpService.addFeedback(comment, this.guidelineId, this.version)
          : comment.type === CommentType.PersonalNote
            ? this.commentsHttpService.addPersonalNote(comment, this.guidelineId, this.version)
            : this.commentsHttpService.addComment(comment, this.guidelineId, this.version)
        ).pipe(
          catchError((error) => {
            return throwError(() => ({ ...error, commentId: comment.id }));
          }),
        ),
      ),
      tapAllResponses<Comment, HttpErrorResponse & { commentId: string }>(
        (comment) => {
          if ((this._comments() || []).find((newComment) => newComment.id === comment.id)) {
            this.editCommentLocally(comment);
          } else {
            this.addCommentLocally(comment);
          }

          this.selectCommentByCommentId(comment.id);
        },
        (errorWithId) => {
          this.deleteCommentLocally(errorWithId.commentId);
          this.messagesService.showError(undefined, errorWithId);
        },
      ),
    ),
  );

  private _editComment = rxMethod<AnchoredComment | Comment>((comment$) =>
    comment$.pipe(
      switchMap((comment) => {
        return this.commentsHttpService.editComment(comment);
      }),
      tapAllResponses<AnchoredComment>(
        (comment) => {
          this.editCommentLocally(comment);
        },
        (error) => {
          this.messagesService.showError(undefined, error);
        },
      ),
    ),
  );

  private _deleteComment = rxMethod<Comment>((comment$) =>
    comment$.pipe(
      switchMap((comment) =>
        this.commentsHttpService.deleteComment(comment.id).pipe(
          map((result) => ({ result, comment })),
          catchError((error) => throwError(() => ({ error, comment }))),
        ),
      ),
      tapAllResponses<{ result: string; comment: Comment }, { error: HttpErrorResponse; comment: Comment }>(
        () => {
          console.debug(`Comment deleted successfully from server.`);
        },
        (errObj) => {
          this.addCommentLocally(errObj.comment);
          this.selectedCommentId.set(errObj.comment.id);

          this.messagesService.showError(undefined, errObj.error);
        },
      ),
    ),
  );

  constructor() {
    effect(() => {
      const selected = this.selectedCommentData();
      if (!selected) return;
      if (this.isOriginalContentUnavailable(selected)) return;
      if (selected.anchor.type === 'flowchart-element' && !this.isCommentsBarOpen()) return;

      const container = document.querySelector('.tt-commentable-container');
      if (!container) return;

      const commentElement = container.querySelector<HTMLElement>(`.tt-comments-comment-${selected.id}`);
      if (!commentElement) {
        return;
      }

      commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    effect(() => {
      const selectedComment = this.selectedCommentData();

      document.querySelectorAll('.tt-comments-comment--selected').forEach((el) => el.classList.remove('tt-comments-comment--selected'));

      if (selectedComment && !this.isOriginalContentUnavailable(selectedComment)) {
        document.querySelectorAll(`.tt-comments-comment-${selectedComment.id}`).forEach((element) => {
          element.classList.add('tt-comments-comment--selected');
        });
      }
    });

    effect(() => {
      const expandService = this.expandService;
      const selected = this.selectedCommentData();
      const anchor = selected?.anchor;
      if (selected && this.isOriginalContentUnavailable(selected)) return;

      if (!expandService || !anchor) {
        return;
      }

      let id = anchor.elementId ?? anchor.widgetId;
      if (!id) {
        return;
      }

      // TODO: temporary solution for the component store
      id = id.replace(/-(title|description|description-text)$/, '');

      expandService.setExpandState(id, true);
    });
  }

  public init(
    guidelineId: string,
    version: string,
    {
      readonly,
      batchEdit,
      comments,
      headless,
      newCommentType,
      newCommentTypes,
      preserveReaderFeedbackAnchors,
      isAnchorAvailable,
    }: Partial<CommentServiceInitOptions>,
  ) {
    this.batchEdit = batchEdit;
    this.guidelineId = guidelineId;
    this.version = version;
    this.selectedCommentId.set(undefined);
    this._isCommentsBarOpen.set(false);

    if (headless !== undefined) {
      this.headless = headless;
    }

    this._newCommentType.set(newCommentType ?? CommentType.Comment);
    this.newCommentTypes = newCommentTypes;
    this.preserveReaderFeedbackAnchors = preserveReaderFeedbackAnchors ?? false;
    this.anchorValidator.set(isAnchorAvailable);

    if (readonly === false) {
      this._readonly.set(false);
    }

    this._initialized.set(true);

    const initialComments = comments ?? [];
    this._comments.set(initialComments);
    this._originalComments.set(initialComments);
  }

  public getNewCommentTypes(): CommentType[] {
    if (this.newCommentTypes) {
      return this.newCommentTypes;
    }

    return this._newCommentType() === CommentType.ReaderFeedback
      ? [CommentType.ReaderFeedback, CommentType.PersonalNote]
      : [CommentType.Comment];
  }

  public setComments(comments: AnchoredComment[]) {
    this._comments.set(comments);
    this._originalComments.set(comments);
  }

  public editComment(comment: AnchoredComment | Comment) {
    if (this.shouldPreserveAnchor(comment) && 'anchor' in comment) {
      const existingComment = this.findCommentOrReplyById(comment.id);
      if (existingComment && 'anchor' in existingComment) {
        comment = { ...comment, anchor: existingComment.anchor };
      }
    }

    this.editCommentLocally(comment);

    if (this.batchEdit && comment.type === CommentType.Comment) {
      return;
    }

    if (comment.createdAt) {
      this._editComment(comment);
    } else {
      this._addComment(comment);
    }
  }

  public addComment(comment: AnchoredComment | Comment) {
    if (this._readonly()) {
      return;
    }

    this.addCommentLocally(comment);
  }

  public deleteCommentsByAnchorWidgetId(widgetId: string) {
    if (this._readonly()) {
      return;
    }

    const commentsToDelete = (this._comments() || [])
      .filter((c) => c.anchor.widgetId === widgetId && !this.shouldPreserveAnchor(c))
      .map((c) => c.id);

    for (const commentId of commentsToDelete) {
      this.deleteCommentLocally(commentId);

      if (!this.batchEdit) {
        const comment = this.findCommentOrReplyById(commentId);

        if (comment.createdAt) {
          this._deleteComment(comment);
        }
      }
    }
  }

  public deleteComment(commentId: string) {
    if (this._readonly()) {
      return;
    }

    const comment = this.findCommentOrReplyById(commentId);

    this.deleteCommentLocally(comment.id);

    if (this.batchEdit && comment.type === CommentType.Comment) {
      return;
    }

    if (comment.createdAt) {
      this._deleteComment(comment);
    }
  }

  private editCommentLocally(comment: AnchoredComment | Comment) {
    if (this._readonly()) {
      return;
    }

    this._comments.update((comments) =>
      produce(comments, (draft) => {
        if (!draft) return;

        let wasFound = false;
        const now = new Date();

        const handleUpdate = (target: any) => {
          const oldText = target.text;
          const newText = (comment as any).text;

          const textChanged = !compareHtmlText(oldText, newText);

          if (!target.createdAt) {
            target.createdAt = now;
          } else if (textChanged) {
            target.updatedAt = now;
          }

          Object.assign(target, comment);
        };

        for (const rootComment of draft) {
          if ('anchor' in comment && comment.anchor) {
            if (rootComment.id === comment.id) {
              handleUpdate(rootComment);
              wasFound = true;
              break;
            }
          } else {
            if (rootComment.replies) {
              const reply = rootComment.replies.find((r) => r.id === comment.id);
              if (reply) {
                handleUpdate(reply);
                wasFound = true;
                break;
              }
            }
          }
        }

        if (!wasFound) {
          console.warn(`Comment with id ${comment.id} not found for update.`);
        }
      }),
    );
  }

  public addCommentLocally(comment: AnchoredComment | Comment) {
    if (this._readonly()) {
      return;
    }

    this._comments.update((existingComments) =>
      produce(existingComments ?? [], (draft) => {
        if (draft.length === 0) {
          draft.push({
            ...(comment as AnchoredComment),
            createdBy: this.currentUser(),
          });
          return;
        }

        if ('anchor' in comment && comment.anchor) {
          draft.push({
            ...comment,
            createdBy: this.currentUser(),
          });
          return;
        }

        const parentId = comment.parentId;
        const parent = draft.find((c) => c.id === parentId);

        if (parent) {
          if (!parent.replies) parent.replies = [];
          parent.replies.push(comment);
        }
      }),
    );

    this.selectCommentByCommentId(comment.parentId ?? comment.id);
  }

  private deleteCommentLocally(commentId: string) {
    if (this._readonly()) {
      return;
    }

    const comment = this.findCommentOrReplyById(commentId);
    if (!comment) {
      console.warn(`Comment with id ${commentId} not found for removal.`);
      return;
    }

    if (!this.headless) {
      if ('anchor' in comment && comment.anchor) {
        if (comment.anchor.type === 'text') {
          const markTags = document.querySelectorAll(`mark.tt-comments-comment-${comment.id}`);
          markTags.forEach((tag) => {
            const parent = tag.parentNode;
            if (parent) {
              while (tag.firstChild) {
                parent.insertBefore(tag.firstChild, tag);
              }
              parent.removeChild(tag);
            }
          });
        } else if (comment.anchor.type === 'element') {
          const container = document.querySelector<HTMLElement>(`.tt-commentable-${comment.anchor.widgetId}-${comment.anchor.elementId}`);
          if (container) unmarkCommentOnContainer(container);
        }
      }
    }

    this._comments.update((comments) =>
      produce(comments, (draft) => {
        if (!draft) return;

        const topIndex = draft.findIndex((c) => c.id === commentId);
        if (topIndex !== -1) {
          draft.splice(topIndex, 1);
          return;
        }

        for (const comment of draft) {
          if (comment.replies) {
            const replyIndex = comment.replies.findIndex((r) => r.id === commentId);
            if (replyIndex !== -1) {
              comment.replies.splice(replyIndex, 1);
              break;
            }
          }
        }
      }),
    );

    if (this.selectedCommentId() === commentId) {
      this.selectedCommentId.set(undefined);
    }
  }

  public selectCommentByFlowchartElementId(elementId: string, widgetId?: string) {
    const comments = (this._comments() || []).filter(
      (comment): comment is AnchoredFlowchartComment =>
        comment.anchor.type === 'flowchart-element' &&
        comment.anchor.elementId === elementId &&
        (!widgetId || comment.anchor.widgetId === widgetId),
    );

    if (!comments.length) {
      if (!this.selectedComment()) {
        this.selectCommentByCommentId();
      }
      return;
    }

    const selectedIndex = comments.findIndex((comment) => comment.id === this.selectedCommentId());
    const nextIndex = selectedIndex >= 0 ? (selectedIndex + 1) % comments.length : 0;
    this.selectCommentByCommentId(comments[nextIndex].id);
  }

  public getAnchorThreadPosition(commentId: string): { current: number; total: number } | null {
    const comment = (this._comments() || []).find((candidate) => candidate.id === commentId);
    if (!comment?.anchor) {
      return null;
    }

    const anchor = comment.anchor;
    const commentsOnAnchor = (this._comments() || []).filter((candidate) => {
      const candidateAnchor = candidate.anchor;
      return (
        candidate.type === comment.type &&
        candidateAnchor.type === anchor.type &&
        candidateAnchor.widgetId === anchor.widgetId &&
        candidateAnchor.elementId === anchor.elementId &&
        (anchor.type !== 'text' ||
          (candidateAnchor.type === 'text' && candidateAnchor.start === anchor.start && candidateAnchor.length === anchor.length))
      );
    });

    if (commentsOnAnchor.length <= 1) {
      return null;
    }

    return {
      current: commentsOnAnchor.findIndex((candidate) => candidate.id === commentId) + 1,
      total: commentsOnAnchor.length,
    };
  }

  public selectCommentByCommentId(commentId?: string) {
    const currentlySelectedComment = this.selectedComment()?.comment;

    if (currentlySelectedComment && !currentlySelectedComment.createdAt && currentlySelectedComment.text.trim() === '') {
      this.deleteCommentLocally(currentlySelectedComment.id);
    }

    this.selectedCommentId.set(commentId);
  }

  public openCommentsBar(commentId?: string): boolean {
    const comments = this._comments() ?? [];
    if (comments.length === 0) {
      return false;
    }

    if (commentId) {
      const commentToOpen = comments.find((comment) => comment.id === commentId);
      if (!commentToOpen?.id) {
        return false;
      }

      if (commentToOpen.id !== this.selectedCommentId()) {
        this.selectCommentByCommentId(commentToOpen.id);
      }
    }

    this.refreshCommentOrder();
    this._isCommentsBarOpen.set(true);
    return true;
  }

  public closeCommentsBar(): void {
    this._isCommentsBarOpen.set(false);
    this.selectCommentByCommentId();
  }

  private selectCommentByOffset(offset: number) {
    const comments = this.orderedComments();
    if (!comments.length) return;

    const currentIndex = comments.findIndex((c) => c.id === this.selectedCommentId());
    const newIndex = (currentIndex + offset + comments.length) % comments.length;

    this.selectCommentByCommentId(comments[newIndex].id);
  }

  public selectPreviousComment() {
    this.selectCommentByOffset(-1);
  }

  public selectNextComment() {
    this.selectCommentByOffset(1);
  }

  public replyToComment(commentId: string, reply: string) {
    if (this._readonly()) {
      return;
    }

    const replyComment: Comment = {
      id: crypto.randomUUID(),
      createdBy: this.currentUser(),
      createdAt: new Date(),
      text: reply,
      parentId: commentId,
      type: this.findCommentOrReplyById(commentId)?.type ?? this._newCommentType(),
    };

    this.addCommentLocally(replyComment);

    if (!this.batchEdit || replyComment.type !== CommentType.Comment) {
      this._addComment(replyComment);
    }
  }

  public isOriginalContentUnavailable(comment: Comment | AnchoredComment): boolean {
    return (
      comment.type === CommentType.PersonalNote && 'anchor' in comment && !!comment.anchor && this.anchorValidator()?.(comment) === false
    );
  }

  public shouldPreserveAnchor(comment: Comment | AnchoredComment): boolean {
    return comment.type === CommentType.PersonalNote || (this.preserveReaderFeedbackAnchors && comment.type === CommentType.ReaderFeedback);
  }

  private findCommentOrReplyById(id: string): Comment | AnchoredComment | null {
    for (const comment of this._comments() || []) {
      if (comment.id === id) {
        return comment;
      }

      if (comment.replies) {
        const reply = comment.replies.find((r) => r.id === id);
        if (reply) {
          return reply;
        }
      }
    }

    return null;
  }
}
